<script lang="ts">
import { computed, defineComponent, onActivated, onMounted, reactive, watch } from 'vue'
import LenOptionChildRender from './child/LenOptionChild.vue'
import CardHeader from '@/components/ui/card/CardHeader.vue'
import { useCustomization } from '@/hooks/useCustomization'
import { useEnhancer } from '@/app/enhancer'
import { lenOptions } from '@/hooks/enums'
import { EventType, useEvents } from '@/hooks/useEvents'

export default defineComponent({
  name: 'GLenOptions',
  components: { CardHeader, LenOptionChildRender },
  emits: ['ok', 'back'],
  setup(_, { emit }) {
    const customization = useCustomization()
    const { storeFavorites } = useEnhancer()
    const eventBus = useEvents()

    const state = reactive({
      primaryOptions: '',
      secondaryOptions: '',
      thirdOptions: '',
    })

    const currentLensOptions = computed(() => storeFavorites.selectedLenOption)

    const lensOptions = computed<AnyObject[]>(() => {
      const selectedLen = storeFavorites.selectedLen
      return customization.getLensOptions.value.map((options) => {
        const disabled = options.excludes.includes(selectedLen.value)
        return {
          ...options,
          disabled,
        }
      })
    })

    const primaryOptions = computed<AnyObject[]>(() => {
      return lensOptions.value.slice(0, 2)
    })

    const secondaryOptions = computed<AnyObject[]>(() => {
      return lensOptions.value.slice(2, 4)
    })

    const thirdOptions = computed<AnyObject[]>(() => {
      return lensOptions.value.slice(4)
    })

    const isCurrent = item => currentLensOptions.value.includes(item.key)

    // ! The matrix -> https://docs.google.com/spreadsheets/d/1C0_-vWTnvmCXsmY61MX8kOT0Q7jNUBniMA5aBoetYdo/edit#gid=1197717809
    const onSelect = (item: AnyObject, key) => {
      if (item.disabled)
        return

      // ? Exception-1: Clear when NONE
      if (item.key === lenOptions.NONE) {
        Object.assign(state, {
          primaryOptions: lenOptions.NONE,
          secondaryOptions: '',
          thirdOptions: '',
        })
        return
      }

      // ? Exception-2: Cannot select both BDC and MIRROR
      if (
        item.key === lenOptions.BDC
        && state.secondaryOptions === lenOptions.MIRROR
      ) {
        state.secondaryOptions = ''
        state[key] = state[key] === item.key ? '' : item.key
        return
      }

      if (
        item.key === lenOptions.MIRROR
        && state.thirdOptions === lenOptions.BDC
      ) {
        state.thirdOptions = ''
        state[key] = state[key] === item.key ? '' : item.key
        return
      }
      // ? ============================

      // ? Exception-3: Clear when primary is NONE
      if (state.primaryOptions === lenOptions.NONE)
        state.primaryOptions = ''

      // ? Set selection toggle
      state[key] = state[key] === item.key ? '' : item.key
    }

    // TODO: Set initial lens options state
    const initialLensOptions = () => {
      currentLensOptions.value.forEach((key: any) => {
        const option: any = lensOptions.value.find(item => item.key === key)
        if ([lenOptions.NONE, lenOptions.LUTINA].includes(key))
          state.primaryOptions = option?.disabled ? '' : key
        else if ([lenOptions.MIRROR, lenOptions.STRONGEST].includes(key))
          state.secondaryOptions = option?.disabled ? '' : key
        else
          state.thirdOptions = option?.disabled ? '' : key
      })

      // If empty, set NONE
      if (state.primaryOptions === '' && state.secondaryOptions === '')
        state.primaryOptions = lenOptions.NONE
    }

    watch(state, () => {
      const combineOptions = [
        state.primaryOptions,
        state.secondaryOptions,
        state.thirdOptions,
      ].filter(Boolean)
      storeFavorites.setCurrentConfig('lensOptions', combineOptions)
    })

    onActivated(() => {
      initialLensOptions()
    })

    onMounted(() => {
      // TODO: on LENS_SELECTED
      eventBus.on(EventType.LENS_SELECTED, () => {
        initialLensOptions()
      })
    })

    return {
      primaryOptions,
      secondaryOptions,
      thirdOptions,
      onSelect,
      isCurrent,
      handleClose: () => {
        emit('back')
      },
    }
  },
})
</script>

<template>
  <div class="animate_500">
    <CardHeader
      title="オプションを追加する"
      hidden-left
      @close="handleClose"
    />
    <div class="row q-col-gutter-sm q-px-md q-py-sm">
      <LenOptionChildRender
        v-for="item in primaryOptions"
        :key="item.key"
        :item="item"
        :is-current="isCurrent(item)"
        @selected="(item) => onSelect(item, 'primaryOptions')"
      />

      <LenOptionChildRender
        v-for="item in secondaryOptions"
        :key="item.key"
        :item="item"
        :is-current="isCurrent(item)"
        @selected="(item) => onSelect(item, 'secondaryOptions')"
      />

      <LenOptionChildRender
        v-for="item in thirdOptions"
        :key="item.key"
        :item="item"
        :is-current="isCurrent(item)"
        @selected="(item) => onSelect(item, 'thirdOptions')"
      />
    </div>
  </div>
</template>
